import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState, useMemo } from 'react'
import { Button, Card, Col, Form, InputGroup, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap'
import toast from 'react-hot-toast';
import Select from "react-select"
import { ClientService } from '../../component/services/client.service';
import { faRefresh, faSearch, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import CetLoader from '../../component/Loader/CetLoader';
import { ClientServiceV2 } from '../../component/services/client.service.v2';

type SortKey = 'centerPreference' | 'allocatedCenterId' | 'state' | 'zone' | 'city' | 'attendedCount' | 'percent';
type SortDir = 'asc' | 'desc' | null;

export default function DBSSync() {

    const [selectValue, setSelectValue] = useState<any>();
    const [shifts, setShifts] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [centerData, setCenterData] = useState<any>([]);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>(null);

    const [loadingModal, setLoadingModal] = useState<boolean>(false);

    const getAllShift = async () => {
        await ClientService.getAllShifts().then(res => {
            if (res.status === 200) {
                setShifts(res.data.shifts.map((data: any) => ({
                    label: data.shiftName + " - " + moment(data.startTime).format("DD MMM YYYY"),
                    value: data._id
                })));
            }
        }).catch(err => {
            toast.error(err.response.data);
        });
    };

    const getData = async () => {
        if (!selectValue) {
            toast.error("Please select a shift");
            return;
        }
        setLoading(true);
        await ClientServiceV2.getCenterStats(selectValue)
            .then((res: any) => {
                if (res.status === 200) {
                    setCenterData(res.data?.data || []);
                }
            })
            .catch((err: any) => {
                toast.error(err.response.data);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSync = async (center: any) => {
        setLoadingModal(true);
        await ClientServiceV2.dbsSyncCenter(selectValue, center)
            .then((res: any) => {
                if (res.status === 200) {
                    toast.success(res.data.message);
                }
            })
            .catch((err: any) => {
                toast.error(err.response.data);
            })
            .finally(() => {
                setLoadingModal(false);
            });
    };

    useEffect(() => { getAllShift(); }, []);
    useEffect(() => { if (selectValue) getData(); }, [selectValue]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
            if (sortDir === 'desc') setSortKey(null);
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const SortIcon = ({ col }: { col: SortKey }) => {
        if (sortKey !== col) return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted opacity-50" style={{ fontSize: 11 }} />;
        return <FontAwesomeIcon icon={sortDir === 'asc' ? faSortUp : faSortDown} className="ms-1 text-primary" style={{ fontSize: 11 }} />;
    };

    const processedData = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();

        let filtered = centerData;
        if (q) {
            filtered = centerData.filter((d: any) =>
                [d?.centerPreference, d?.allocatedCenterId, d?.state, d?.zone, d?.city]
                    .some(val => String(val ?? '').toLowerCase().includes(q))
            );
        }

        if (sortKey && sortDir) {
            filtered = [...filtered].sort((a: any, b: any) => {
                let aVal: any, bVal: any;
                if (sortKey === 'percent') {
                    aVal = (a.attendedCount / a.totalCount) * 100 || 0;
                    bVal = (b.attendedCount / b.totalCount) * 100 || 0;
                } else if (sortKey === 'attendedCount') {
                    aVal = a.attendedCount;
                    bVal = b.attendedCount;
                } else {
                    aVal = String(a?.[sortKey] ?? '').toLowerCase();
                    bVal = String(b?.[sortKey] ?? '').toLowerCase();
                }
                if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [centerData, searchQuery, sortKey, sortDir]);

    const SortableTh = ({ label, col }: { label: string; col: SortKey }) => (
        <th
            style={{ fontSize: 14, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => handleSort(col)}
        >
            {label} <SortIcon col={col} />
        </th>
    );

    return (
        <>
            <Row className="mt-5 text-left">
                <Col md={8}>
                    <h4 className="text-secondary mt-3 fw-bold">DBS Syncing</h4>
                </Col>
                <Col md={4}>
                    <Form.Label className="text-muted fw-bold">Select Shift</Form.Label>
                    <Select
                        options={shifts}
                        placeholder="Select Shifts"
                        onChange={(e: any) => setSelectValue(e?.value)}
                        isClearable
                        value={selectValue ? shifts?.find((shift: any) => shift.value === selectValue) : null}
                    />
                </Col>
            </Row>

            {loading ? <CetLoader /> :
                <Row>
                    <Col>
                        <Card
                            className="mt-3"
                            style={{ backgroundColor: "#f9f9f9", borderRadius: "12px", border: "none" }}
                        >
                            <Card.Body>

                                <Row className="mt-2 align-items-center">
                                    <Col md={5}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-white border-end-0">
                                                <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search by venue, state, division, city…"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                className="border-start-0"
                                                style={{ boxShadow: 'none' }}
                                            />
                                            {searchQuery && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setSearchQuery('')}
                                                >
                                                    ✕
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    <Col className="d-flex justify-content-end gap-2">
                                        {(sortKey || searchQuery) && (
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => { setSortKey(null); setSortDir(null); setSearchQuery(''); }}
                                            >
                                                Clear Filters
                                            </Button>
                                        )}
                                        <Button size="sm" onClick={getData} variant="outline-secondary" className="d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faRefresh} /> Reload
                                        </Button>
                                    </Col>
                                </Row>

                                {centerData.length > 0 && (
                                    <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                                        Showing {processedData.length} of {centerData.length} venues
                                        {sortKey && <span className="ms-2">· Sorted by <strong>{sortKey}</strong> ({sortDir})</span>}
                                    </div>
                                )}

                                <Table striped hover responsive className="mt-3">
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 14 }}>Sr. No</th>
                                            <SortableTh label="Venue ID" col="centerPreference" />
                                            <SortableTh label="Venue Name" col="allocatedCenterId" />
                                            <SortableTh label="State" col="state" />
                                            <SortableTh label="Division" col="zone" />
                                            <SortableTh label="City" col="city" />
                                            <SortableTh label="Present/Scheduled" col="attendedCount" />
                                            <SortableTh label="Present (%)" col="percent" />
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={10} className="text-center">
                                                    <Spinner animation="border" />
                                                </td>
                                            </tr>
                                        ) : processedData.length > 0 ? (
                                            processedData.map((data: any, index: number) => {
                                                const percent = (data?.attendedCount / data?.totalCount) * 100 || 0;
                                                const progressVariant = percent >= 90 ? 'success' : percent >= 75 ? 'warning' : 'danger';
                                                return (
                                                    <tr key={data?._id ?? index}>
                                                        <td>{index + 1}</td>
                                                        <td>{data?.centerPreference}</td>
                                                        <td>{data?.allocatedCenterId}</td>
                                                        <td>{data?.state}</td>
                                                        <td>{data?.zone}</td>
                                                        <td>{data?.city}</td>
                                                        <td>{data?.attendedCount}/{data?.totalCount}</td>
                                                        <td style={{ width: '300px' }}>
                                                            <div className="d-flex align-items-center">
                                                                <div className="flex-grow-1 me-3">
                                                                    <ProgressBar
                                                                        now={percent}
                                                                        variant={progressVariant}
                                                                        style={{ height: '8px' }}
                                                                    />
                                                                </div>
                                                                <span
                                                                    className={`fw-bold text-${progressVariant}`}
                                                                    style={{ fontSize: '14px', minWidth: '45px' }}
                                                                >
                                                                    {percent.toFixed(2)}%
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Button size="sm" variant="outline-primary" onClick={() => handleSync(data?.centerPreference)}>
                                                                Sync
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={10} className="text-center text-muted py-4">
                                                    {searchQuery ? `No results for "${searchQuery}"` : 'No Data Available'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                    <LoadingModal show={loadingModal} handleClose={() => setLoadingModal(false)} />
                </Row>
            }
        </>
    );
}


interface IProps {
    show: boolean;
    handleClose: () => void;
}

export function LoadingModal({ show, handleClose }: IProps) {
    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                size="lg"
                animation={true}
                backdrop="static"
                centered
            >
                <Modal.Header>
                    <Modal.Title>Please wait</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <CetLoader message="Syncing Data, Please wait" />
                </Modal.Body>
            </Modal>
        </>
    )

}